//pages/Menu/Menu.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async"; // Keep this for page SEO
// CSS
import "./menu-page.css";

// Sections
import MenuHero from "../../components/MenuHero/MenuHero";
import RestaurantPopularItems from "../../components/RestaurantPopularItems/RestaurantPopularItems";
import FullMenu from "../../components/FullMenu/FullMenu";
import { API_BASE_URL } from "../../config";

// Scoped to one restaurant (resolved from the :slug URL param) - a cart
// can only hold one restaurant's items at a time (see FullMenu/
// SelectedMenuItem), so this is the page that establishes which one.
const Menu = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  // Default to one real category instead of "All" - with 58+ items,
  // opening on an unfiltered mix of breakfast/drinks/dinner together was
  // both a poor first impression and what made the page render ~11,500px
  // tall with every item's backdrop-blur active at once. A direct link
  // (e.g. from the homepage) can still request a specific category.
  const initialCategory = queryParams.get("category") || "lunch";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [restaurant, setRestaurant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchRestaurantAndItems = async () => {
      try {
        const restaurantRes = await axios.get(`${API_BASE_URL}/restaurant/slug/${slug}`);
        const found = restaurantRes.data.restaurant;
        setRestaurant(found);

        const itemsRes = await axios.get(`${API_BASE_URL}/menu/get-items`, {
          params: { restaurant: found._id },
        });
        setItems(itemsRes.data.items);
      } catch (error) {
        console.error("Error fetching restaurant menu:", error);
        setNotFound(true);
      }
    };
    fetchRestaurantAndItems();
  }, [slug]);

  useEffect(() => {
    setSelectedCategory(initialCategory);

    if (location.hash === "#full-menu") {
      const fullMenuSection = document.getElementById("full-menu");
      if (fullMenuSection) {
        fullMenuSection.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [initialCategory, location.hash]);

  if (notFound) {
    return (
      <main className="menu-page">
        <div className="section-container text-center py-16">
          <h2 className="section-heading text-dark mb-4">Restaurant not found</h2>
          <button className="btn btn-primary" onClick={() => navigate("/")}>
            Back to restaurants
          </button>
        </div>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        {/* Page-specific SEO tags only */}
        <title>{restaurant ? `${restaurant.name} Menu | Ekhoni Khabo` : "Menu | Ekhoni Khabo"}</title>
        <meta
          name="description"
          content="Browse our extensive menu of appetizers, main courses, desserts, and beverages. Order online for fast delivery!"
        />
        <meta property="og:title" content={restaurant ? `${restaurant.name} Menu` : "Ekhoni Khabo Menu"} />
        <meta
          property="og:description"
          content="Check out our delicious restaurant menu"
        />
      </Helmet>

      <main className="menu-page">
        <MenuHero restaurant={restaurant} />
        <RestaurantPopularItems items={items} restaurant={restaurant} />
        <FullMenu selectedCategory={selectedCategory} items={items} restaurant={restaurant} />
      </main>
    </>
  );
};

export default Menu;
